// Pure helpers for PlanYear date logic — used by the create/edit
// modal and by the inline-edit fields on the PlanYear detail
// page. Kept side-effect-free so tests can exercise the rules
// without rendering the form.


/**
 * Default ``effective_end`` for a given ``effective_start``: one
 * year later, minus one day. Mirrors what brokers expect when they
 * type a renewal anchor:
 *
 *   06/14/2026 → 06/13/2027
 *   01/01/2026 → 12/31/2026
 *
 * Returns ``""`` when ``effective_start`` is empty/invalid so the
 * caller can leave the input blank instead of showing
 * ``"Invalid date"``.
 */
export function deriveDefaultEffectiveEnd(effectiveStart: string): string {
  if (!effectiveStart) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(effectiveStart);
  if (!m) return "";
  const startYear = Number(m[1]);
  const startMonth = Number(m[2]);
  const startDay = Number(m[3]);
  // Use UTC to avoid any timezone shift surprises — the inputs are
  // pure dates with no time-of-day component.
  const startUtc = Date.UTC(startYear, startMonth - 1, startDay);
  if (Number.isNaN(startUtc)) return "";
  // ``+1 year, -1 day`` keeps Jan 1 → Dec 31 and June 14 → June 13.
  const next = new Date(startUtc);
  next.setUTCFullYear(next.getUTCFullYear() + 1);
  next.setUTCDate(next.getUTCDate() - 1);
  const yyyy = next.getUTCFullYear();
  const mm = String(next.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(next.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}


export interface PlanYearDateValidationInput {
  effective_start: string;
  effective_end: string;
  open_enrollment_start: string;
  open_enrollment_end: string;
}


export interface PlanYearDateValidationResult {
  ok: boolean;
  error: string | null;
}


/**
 * Validate the four date inputs as a set:
 *
 * - ``effective_end`` must be strictly after ``effective_start``.
 * - When both ``open_enrollment_*`` are present,
 *   ``open_enrollment_end`` must be on/after ``open_enrollment_start``.
 *
 * Empty strings are treated as "not set"; the helper returns
 * ``ok: true`` so the form can let the parent decide whether
 * required-field errors apply.
 */
export function validatePlanYearDates(
  values: PlanYearDateValidationInput,
): PlanYearDateValidationResult {
  if (values.effective_start && values.effective_end) {
    if (values.effective_end <= values.effective_start) {
      return {
        ok: false,
        error: "Effective end must be after effective start.",
      };
    }
  }
  if (values.open_enrollment_start && values.open_enrollment_end) {
    if (values.open_enrollment_end < values.open_enrollment_start) {
      return {
        ok: false,
        error:
          "Open enrollment end must be on or after open enrollment start.",
      };
    }
  }
  return { ok: true, error: null };
}
